import { useCallback, useEffect, useRef, useState } from 'react';
import { m } from 'motion/react';
import {
  SuperBarViewState,
  useChatContext,
  SystemMessageState,
} from './context/use-chat-context';
import type {
  DisplayFilterGroup,
  Store,
  StoreSettings,
} from './services/types';
import { cn } from './utils/styles';
import { getHotkeyHandler, useMediaQuery } from '@mantine/hooks';
import { useStoreSearchOverride } from './hooks/useStoreSearchOverride';
import { useUserContext } from './context/use-user-context';
import { InputActionButtons } from './InputActionButtons';
import { AiIconDisplay } from './AiIconDisplay';
import { SubmitButton } from './SubmitButton';
import { TruncatedTextOverlay } from './TruncatedTextOverlay';
import { CTAButtons } from './CTAButtons';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { useTextOverflow } from './hooks/useTextOverflow';
import { useVoiceTranscript } from './hooks/useVoiceTranscript';
import type { VoiceModeState } from './hooks/useVoiceMode';
import type { AudioAnalyserService } from './services/audio-analyser.service';
import { VoiceModeButton } from './VoiceModeButton';

const TEXTAREA_EXPANDED_HEIGHT = '110px';
const TEXTAREA_COLLAPSED_HEIGHT = '60px';
const TYPING_DEBOUNCE_MS = 300;

export type VoiceProps = {
  voiceModeState?: VoiceModeState;
  toggleVoiceMode?: () => void;
  exitVoiceMode?: (
    reason?: 'toggle' | 'typed' | 'error' | 'silence' | 'max-turns'
  ) => void;
  isVoiceModeAvailable?: boolean;
  analyserService?: AudioAnalyserService | null;
  isListening: boolean;
  transcript: string;
  isVoiceSupported: boolean;
  micPermissionDenied: boolean;
  speechServiceBlocked: boolean;
  startListening: () => void;
  stopListening: () => void;
};

type SuperbarInputProps = {
  value: string;
  setValue: (value: string) => void;
  loading: boolean;
  sendChatMessage: (message: {
    newMessage?: string;
    filterGroups: DisplayFilterGroup[];
  }) => Promise<unknown>;
  store?: Store;
  storeSettings?: StoreSettings;
  handleClearAllAIFilters: () => void;
  isCompactView: boolean;
  setIsCompactView: (isCompactView: boolean) => void;
  voice: VoiceProps;
};

export const SuperbarInput = ({
  value,
  setValue,
  loading,
  sendChatMessage,
  store,
  storeSettings,
  handleClearAllAIFilters,
  isCompactView,
  setIsCompactView,
  voice,
}: SuperbarInputProps) => {
  const {
    voiceModeState,
    toggleVoiceMode,
    exitVoiceMode,
    isVoiceModeAvailable,
    analyserService,
    isListening,
    transcript,
    isVoiceSupported,
    micPermissionDenied,
    speechServiceBlocked,
    startListening,
    stopListening,
  } = voice;
  const { voiceInputEnabled } = useUserContext();
  const {
    superBarViewState,
    setSuperBarViewState,
    appliedFilterGroups,
    systemMessageState,
    isMenuOpen,
    designatedCtas,
    displayMessage,
  } = useChatContext();

  const theme = useSuperbarTheme();
  // For textarea height and UI behavior, only EXPANDED state should be considered expanded
  const isExpanded = superBarViewState === SuperBarViewState.EXPANDED;
  // For width calculations (in ChatView), both EXPANDED and DISPLAYING_ANSWER use expanded width
  const isExpandedWidth =
    isExpanded || superBarViewState === SuperBarViewState.DISPLAYING_ANSWER;

  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [isFocused, setIsFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => clearTimeout(typingTimeoutRef.current);
  }, []);
  const { showTruncatedOverlay, expandIfOverflowing } = useTextOverflow({
    inputRef,
    value,
    isFocused,
    superBarViewState,
    setSuperBarViewState,
  });

  const isVoiceAvailable = isVoiceSupported && voiceInputEnabled;
  const voiceDisabled = micPermissionDenied || speechServiceBlocked;
  const voiceDisabledTooltip = speechServiceBlocked
    ? 'Voice input needs access to a speech service. Disable ad-blockers or shields, or try Chrome/Edge.'
    : undefined;
  const voiceModeDisabled = speechServiceBlocked;
  const voiceModeDisabledTooltip = speechServiceBlocked
    ? 'Voice mode needs access to a speech service. Disable ad-blockers or shields, or try Chrome/Edge.'
    : undefined;

  useVoiceTranscript({
    isListening,
    transcript,
    value,
    setValue,
    expandIfOverflowing,
  });

  // Clear input when voice mode transitions from listening to processing
  // (the transcript was sent as a chat message)
  const prevIsListeningRef = useRef(false);
  useEffect(() => {
    const wasListening = prevIsListeningRef.current;
    prevIsListeningRef.current = isListening;
    if (wasListening && !isListening && voiceModeState === 'processing') {
      setValue('');
    }
  }, [isListening, voiceModeState, setValue]);

  const isVoiceModeActive = !!voiceModeState && voiceModeState !== 'off';
  const isDictating = isListening && !isVoiceModeActive;

  useEffect(() => {
    if (isVoiceModeActive || isDictating) {
      setSuperBarViewState(SuperBarViewState.EXPANDED);
    }
  }, [isVoiceModeActive, isDictating, setSuperBarViewState]);

  const handleVoiceModeStop = useCallback(() => {
    toggleVoiceMode?.();
    if (!value) {
      setSuperBarViewState(SuperBarViewState.DEFAULT);
    }
  }, [toggleVoiceMode, value, setSuperBarViewState]);

  const handleDictationStop = useCallback(() => {
    stopListening();
    if (!value) {
      setSuperBarViewState(SuperBarViewState.DEFAULT);
    }
  }, [stopListening, value, setSuperBarViewState]);

  useStoreSearchOverride({
    enabled: false,
    onSearch: (data) => {
      setValue(data.query);
      sendMessage(data.query);
    },
    store,
  });

  const handleInputClick = useCallback(() => {
    if (isCompactView) {
      setIsCompactView(false);
    }
  }, [isCompactView, setIsCompactView]);

  const sendMessage = useCallback(
    (messageText: string) => {
      if (!messageText) return;

      sendChatMessage({
        newMessage: messageText,
        filterGroups: appliedFilterGroups || [],
      }).finally(() => {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      });
    },
    [sendChatMessage, appliedFilterGroups]
  );

  const primaryCta = designatedCtas.find(
    (cta) => cta.designation === 'primary'
  );
  const secondaryCta = designatedCtas.find(
    (cta) => cta.designation === 'secondary'
  );
  const hasDesignatedCtas = !!primaryCta || !!secondaryCta;

  const isDisplayingMessage = !!displayMessage?.systemAnswer;

  const isInExpandedWidth = [SuperBarViewState.DISPLAYING_ANSWER].includes(
    superBarViewState
  );

  const shouldHaveFullRadiusCorners =
    (!isDisplayingMessage && !isExpanded && !loading) ||
    (isInExpandedWidth && !isDisplayingMessage);

  return (
    <form
      onBlur={(e) => {
        // If the focus moves to something else inside the form (like buttons), don't collapse
        if (
          e.relatedTarget &&
          (e.currentTarget as Node).contains(e.relatedTarget as Node)
        ) {
          return;
        }

        // Keep expanded if there's a display message showing
        if (
          isExpanded &&
          !isVoiceModeActive &&
          !isMenuOpen &&
          !isDisplayingMessage
        ) {
          setSuperBarViewState(SuperBarViewState.DEFAULT);
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        sendMessage(value);
      }}
      className={cn(
        'caddie-input-form caddietw-m-0 caddietw-border-transparent',
        theme.components.inputForm.className,
        isVoiceAvailable && isListening && 'caddie-listening-border'
      )}
      style={{
        backgroundColor: theme.components.inputForm.background,
        borderBottomLeftRadius: shouldHaveFullRadiusCorners
          ? theme.components.inputForm.radiusBottom
          : theme.radii.panel,
        borderBottomRightRadius: shouldHaveFullRadiusCorners
          ? theme.components.inputForm.radiusBottom
          : theme.radii.panel,
        borderTopLeftRadius:
          (systemMessageState === SystemMessageState.HIDDEN &&
            !(isMobile && isMenuOpen)) ||
          loading
            ? shouldHaveFullRadiusCorners
              ? theme.components.inputForm.radiusTopWhenNoSystemMessage
              : theme.radii.panel
            : undefined,
        borderTopRightRadius:
          (systemMessageState === SystemMessageState.HIDDEN &&
            !(isMobile && isMenuOpen)) ||
          loading
            ? shouldHaveFullRadiusCorners
              ? theme.components.inputForm.radiusTopWhenNoSystemMessage
              : theme.radii.panel
            : undefined,
        backdropFilter: theme.components.inputForm.backdropFilter,
        boxShadow:
          isVoiceAvailable &&
          isListening &&
          systemMessageState === SystemMessageState.ASSISTANT
            ? 'none'
            : theme.components.inputForm.boxShadow,
        borderWidth: '2px',
        borderColor: 'transparent',
      }}
    >
      <div
        className={cn(
          'caddietw-flex caddietw-flex-col caddietw-w-full caddietw-relative caddie-style-form-container caddietw-border-none',
          ((isExpanded && hasDesignatedCtas) ||
            isVoiceModeActive ||
            isDictating ||
            !!value) &&
            'caddietw-h-auto'
        )}
      >
        <div className="caddietw-flex caddietw-flex-row caddietw-w-full caddietw-items-start caddietw-relative caddietw-z-10">
          <div
            className={cn(
              'caddietw-items-center caddietw-justify-center caddietw-flex caddietw-pr-[10px] caddietw-border-none caddietw-py-[10px]',
              isExpanded && 'caddietw-!w-[62px] caddietw-pl-[12px]',
              (superBarViewState === SuperBarViewState.DEFAULT ||
                superBarViewState === SuperBarViewState.DISPLAYING_ANSWER) &&
                'caddietw-!w-[52px] caddietw-pl-3',
              superBarViewState === SuperBarViewState.MINIMIZED &&
                'caddietw-!w-[52px] caddietw-pl-3'
            )}
          >
            <AiIconDisplay
              icon={storeSettings?.settings?.icon}
              state={loading ? 'loading' : 'stable'}
              superBarViewState={superBarViewState}
            />
          </div>
          <div className={'caddietw-w-full caddietw-flex caddietw-items-start'}>
            <div className="caddietw-relative caddietw-flex-1 caddietw-min-w-0">
              <m.textarea
                ref={inputRef as any}
                id="caddie-question-input"
                autoFocus={false}
                disabled={loading}
                value={value}
                rows={1}
                placeholder={
                  storeSettings?.defaultPlaceholderText ?? 'Ask me a question'
                }
                autoComplete="off"
                onClick={handleInputClick}
                onFocus={() => {
                  setIsFocused(true);
                  if (!isDisplayingMessage) {
                    setSuperBarViewState(SuperBarViewState.EXPANDED);
                  }
                }}
                onBlur={() => setIsFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(value);
                    return;
                  }
                  getHotkeyHandler([
                    [
                      'mod+Enter',
                      () => setSuperBarViewState(SuperBarViewState.EXPANDED),
                    ],
                  ])(e);
                }}
                onChange={(event) => {
                  const newValue = event.currentTarget.value;
                  setValue(newValue);

                  // Expand if character count exceeds threshold
                  if (
                    newValue.length > 45 &&
                    superBarViewState === SuperBarViewState.DEFAULT
                  ) {
                    setSuperBarViewState(SuperBarViewState.EXPANDED);
                  }
                  if (event.isTrusted) {
                    setIsTyping(true);
                    clearTimeout(typingTimeoutRef.current);
                    typingTimeoutRef.current = setTimeout(
                      () => setIsTyping(false),
                      TYPING_DEBOUNCE_MS
                    );
                  }
                  if (
                    voiceModeState &&
                    voiceModeState !== 'off' &&
                    event.isTrusted
                  ) {
                    exitVoiceMode?.('typed');
                  }
                }}
                className={cn(
                  'caddie-question-input caddie-input-textarea caddie-question-placeholder caddietw-bg-transparent caddietw-z-50 caddietw-w-full caddietw-font-inter caddietw-text-[16px] caddietw-pt-[18px] caddietw-pb-[14px] caddietw-border-none caddietw-outline-none caddietw-px-0 caddietw-shadow-none caddietw-font-normal caddietw-leading-[24px] caddietw-resize-none',
                  theme.components.inputText.className,
                  theme.components.placeholder.className,
                  'caddietw-text-shadow-lg'
                )}
                animate={{
                  height: isExpanded
                    ? TEXTAREA_EXPANDED_HEIGHT
                    : TEXTAREA_COLLAPSED_HEIGHT,
                  minHeight: isExpanded
                    ? TEXTAREA_EXPANDED_HEIGHT
                    : TEXTAREA_COLLAPSED_HEIGHT,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 120,
                  damping: 20,
                  mass: 1,
                }}
                style={
                  {
                    '--caddie-input-color': showTruncatedOverlay
                      ? 'transparent'
                      : loading
                        ? theme.components.inputText.textLoading
                        : theme.components.inputText.text,
                    '--caddie-input-text-shadow': showTruncatedOverlay
                      ? 'none'
                      : theme.components.inputText.textShadow,
                    '--caddie-placeholder-color':
                      theme.components.placeholder.text,
                    paddingTop: '17px',
                    paddingBottom: '20px',
                    resize: 'none',
                    display: 'flex',
                    overflowX: 'hidden',
                    overflowY: isExpanded ? 'auto' : 'hidden',
                  } as React.CSSProperties
                }
              />
              {showTruncatedOverlay && (
                <TruncatedTextOverlay value={value} loading={loading} />
              )}
            </div>
            <InputActionButtons
              isExpanded={isExpanded}
              value={value}
              isTyping={isTyping}
              loading={loading}
              isVoiceAvailable={isVoiceAvailable}
              voice={{
                isListening,
                voiceDisabled,
                voiceDisabledTooltip,
                startListening,
                stopListening,
                voiceModeState,
                toggleVoiceMode,
                isVoiceModeAvailable,
                voiceModeDisabled,
                voiceModeDisabledTooltip,
              }}
              handleClearAllAIFilters={handleClearAllAIFilters}
            />
          </div>
        </div>
        {isExpanded && (
          <m.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{
              duration: 0.4,
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="caddietw-flex caddietw-flex-row caddietw-justify-between caddietw-items-center caddietw-px-4 caddietw-pb-4 caddietw-mt-auto"
          >
            {hasDesignatedCtas ? (
              <CTAButtons primaryCta={primaryCta} secondaryCta={secondaryCta} />
            ) : (
              <div />
            )}
            {isVoiceModeActive ? (
              <VoiceModeButton
                voiceModeState={voiceModeState!}
                onClick={handleVoiceModeStop}
                analyserService={analyserService}
              />
            ) : isDictating ? (
              <VoiceModeButton
                voiceModeState="listening"
                onClick={handleDictationStop}
                analyserService={analyserService}
              />
            ) : (
              <div className="caddietw-flex caddietw-items-center">
                <SubmitButton
                  isLoading={loading}
                  iconColor={
                    theme.components.assistantMessage.ctaButton.background
                  }
                  size={32}
                  disabled={!value}
                />
              </div>
            )}
          </m.div>
        )}
      </div>
    </form>
  );
};
